import type { IanaTimeZone, TimeZone } from './types';

/**
 * IANA timezone reference set. Hand-curated to the country defaults plus the
 * most common per-country alternates (multi-zone countries like the US,
 * Canada, Australia, Russia, Brazil). `offset` is the *standard*-time offset
 * (no DST adjustment); render the live offset client-side via
 * `Intl.DateTimeFormat` if you need to show DST-aware values.
 *
 * Order: by region, then by offset west-to-east. Keeps the dropdown grouped
 * sensibly for users browsing without typeahead.
 */
export const TIMEZONES: readonly TimeZone[] = [
  // ── Europe ────────────────────────────────────────────────────────────
  { iana: 'Atlantic/Reykjavik', label: '(GMT+0:00) Reykjavik', offset: '+00:00', region: 'Europe' },
  { iana: 'Europe/Dublin', label: '(GMT+0:00) Dublin', offset: '+00:00', region: 'Europe' },
  { iana: 'Europe/Lisbon', label: '(GMT+0:00) Lisbon', offset: '+00:00', region: 'Europe' },
  { iana: 'Europe/London', label: '(GMT+0:00) London', offset: '+00:00', region: 'Europe' },
  { iana: 'Europe/Amsterdam', label: '(GMT+1:00) Amsterdam', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Berlin', label: '(GMT+1:00) Berlin', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Brussels', label: '(GMT+1:00) Brussels', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Budapest', label: '(GMT+1:00) Budapest', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Copenhagen', label: '(GMT+1:00) Copenhagen', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Madrid', label: '(GMT+1:00) Madrid', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Oslo', label: '(GMT+1:00) Oslo', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Paris', label: '(GMT+1:00) Paris', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Prague', label: '(GMT+1:00) Prague', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Rome', label: '(GMT+1:00) Rome', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Stockholm', label: '(GMT+1:00) Stockholm', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Vienna', label: '(GMT+1:00) Vienna', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Warsaw', label: '(GMT+1:00) Warsaw', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Zurich', label: '(GMT+1:00) Zurich', offset: '+01:00', region: 'Europe' },
  { iana: 'Europe/Athens', label: '(GMT+2:00) Athens', offset: '+02:00', region: 'Europe' },
  { iana: 'Europe/Bucharest', label: '(GMT+2:00) Bucharest', offset: '+02:00', region: 'Europe' },
  { iana: 'Europe/Helsinki', label: '(GMT+2:00) Helsinki', offset: '+02:00', region: 'Europe' },
  { iana: 'Europe/Istanbul', label: '(GMT+3:00) Istanbul', offset: '+03:00', region: 'Europe' },
  { iana: 'Europe/Kyiv', label: '(GMT+2:00) Kyiv', offset: '+02:00', region: 'Europe' },
  { iana: 'Europe/Moscow', label: '(GMT+3:00) Moscow', offset: '+03:00', region: 'Europe' },

  // ── Americas ──────────────────────────────────────────────────────────
  {
    iana: 'America/Anchorage',
    label: '(GMT-9:00) Anchorage',
    offset: '-09:00',
    region: 'Americas',
  },
  {
    iana: 'America/Los_Angeles',
    label: '(GMT-8:00) Los Angeles',
    offset: '-08:00',
    region: 'Americas',
  },
  {
    iana: 'America/Vancouver',
    label: '(GMT-8:00) Vancouver',
    offset: '-08:00',
    region: 'Americas',
  },
  { iana: 'America/Denver', label: '(GMT-7:00) Denver', offset: '-07:00', region: 'Americas' },
  { iana: 'America/Phoenix', label: '(GMT-7:00) Phoenix', offset: '-07:00', region: 'Americas' },
  { iana: 'America/Chicago', label: '(GMT-6:00) Chicago', offset: '-06:00', region: 'Americas' },
  {
    iana: 'America/Mexico_City',
    label: '(GMT-6:00) Mexico City',
    offset: '-06:00',
    region: 'Americas',
  },
  { iana: 'America/Bogota', label: '(GMT-5:00) Bogota', offset: '-05:00', region: 'Americas' },
  { iana: 'America/Lima', label: '(GMT-5:00) Lima', offset: '-05:00', region: 'Americas' },
  { iana: 'America/New_York', label: '(GMT-5:00) New York', offset: '-05:00', region: 'Americas' },
  { iana: 'America/Toronto', label: '(GMT-5:00) Toronto', offset: '-05:00', region: 'Americas' },
  { iana: 'America/Halifax', label: '(GMT-4:00) Halifax', offset: '-04:00', region: 'Americas' },
  { iana: 'America/Santiago', label: '(GMT-4:00) Santiago', offset: '-04:00', region: 'Americas' },
  {
    iana: 'America/Argentina/Buenos_Aires',
    label: '(GMT-3:00) Buenos Aires',
    offset: '-03:00',
    region: 'Americas',
  },
  {
    iana: 'America/Sao_Paulo',
    label: '(GMT-3:00) Sao Paulo',
    offset: '-03:00',
    region: 'Americas',
  },

  // ── Africa / Middle East ──────────────────────────────────────────────
  { iana: 'Africa/Cairo', label: '(GMT+2:00) Cairo', offset: '+02:00', region: 'Africa' },
  {
    iana: 'Africa/Johannesburg',
    label: '(GMT+2:00) Johannesburg',
    offset: '+02:00',
    region: 'Africa',
  },
  { iana: 'Africa/Lagos', label: '(GMT+1:00) Lagos', offset: '+01:00', region: 'Africa' },
  { iana: 'Africa/Nairobi', label: '(GMT+3:00) Nairobi', offset: '+03:00', region: 'Africa' },
  {
    iana: 'Asia/Jerusalem',
    label: '(GMT+2:00) Jerusalem',
    offset: '+02:00',
    region: 'Middle East',
  },
  { iana: 'Asia/Riyadh', label: '(GMT+3:00) Riyadh', offset: '+03:00', region: 'Middle East' },
  { iana: 'Asia/Dubai', label: '(GMT+4:00) Dubai', offset: '+04:00', region: 'Middle East' },

  // ── Asia / Pacific ────────────────────────────────────────────────────
  { iana: 'Asia/Kolkata', label: '(GMT+5:30) Kolkata', offset: '+05:30', region: 'Asia' },
  { iana: 'Asia/Bangkok', label: '(GMT+7:00) Bangkok', offset: '+07:00', region: 'Asia' },
  { iana: 'Asia/Jakarta', label: '(GMT+7:00) Jakarta', offset: '+07:00', region: 'Asia' },
  { iana: 'Asia/Ho_Chi_Minh', label: '(GMT+7:00) Ho Chi Minh', offset: '+07:00', region: 'Asia' },
  { iana: 'Asia/Hong_Kong', label: '(GMT+8:00) Hong Kong', offset: '+08:00', region: 'Asia' },
  { iana: 'Asia/Kuala_Lumpur', label: '(GMT+8:00) Kuala Lumpur', offset: '+08:00', region: 'Asia' },
  { iana: 'Asia/Manila', label: '(GMT+8:00) Manila', offset: '+08:00', region: 'Asia' },
  { iana: 'Asia/Shanghai', label: '(GMT+8:00) Shanghai', offset: '+08:00', region: 'Asia' },
  { iana: 'Asia/Singapore', label: '(GMT+8:00) Singapore', offset: '+08:00', region: 'Asia' },
  { iana: 'Asia/Seoul', label: '(GMT+9:00) Seoul', offset: '+09:00', region: 'Asia' },
  { iana: 'Asia/Tokyo', label: '(GMT+9:00) Tokyo', offset: '+09:00', region: 'Asia' },
  { iana: 'Australia/Perth', label: '(GMT+8:00) Perth', offset: '+08:00', region: 'Pacific' },
  { iana: 'Australia/Adelaide', label: '(GMT+9:30) Adelaide', offset: '+09:30', region: 'Pacific' },
  { iana: 'Australia/Sydney', label: '(GMT+10:00) Sydney', offset: '+10:00', region: 'Pacific' },
  { iana: 'Pacific/Auckland', label: '(GMT+12:00) Auckland', offset: '+12:00', region: 'Pacific' },

  // ── Universal ─────────────────────────────────────────────────────────
  { iana: 'UTC', label: '(GMT+0:00) UTC', offset: '+00:00', region: 'Universal' },
];

const TIMEZONES_BY_IANA: Map<IanaTimeZone, TimeZone> = new Map(TIMEZONES.map((z) => [z.iana, z]));

/** Look up a timezone by IANA identifier. Returns `undefined` for unknown ids. */
export function findTimeZone(iana: IanaTimeZone): TimeZone | undefined {
  return TIMEZONES_BY_IANA.get(iana);
}
